import { api } from '@/lib/api'
import { defaultPaginationMeta, parseMasterCatalogResponse } from '@/lib/api/masterCatalog'

/** @typedef {import('@/types/api').Service} Service */

export const SKILL_LEVEL_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'standard', label: 'Standard' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'senior', label: 'Senior' },
]

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<{ items: Service[], meta: import('@/types/api').PaginationMeta }>}
 */
export async function fetchServicesPage(params = {}) {
  const { data } = await api.get('/masters/services', { params })
  const parsed = parseMasterCatalogResponse(data, 'services')
  return {
    items: parsed.items,
    meta: defaultPaginationMeta(parsed.meta ?? {}),
  }
}

/**
 * @param {Record<string, unknown>} [params]
 * @returns {Promise<Service[]>}
 */
export async function fetchServicesList(params = {}) {
  const { data } = await api.get('/masters/services', { params })
  return data.data.services
}

/**
 * @param {{ categoryId?: number | null }} [options]
 * @returns {Promise<Service[]>}
 */
export async function fetchServices(options = {}) {
  const params = {}
  if (options.categoryId) {
    params.category_id = options.categoryId
  }
  return fetchServicesList(params)
}

/**
 * @param {number} serviceId
 * @returns {Promise<Service>}
 */
export async function fetchService(serviceId) {
  const { data } = await api.get(`/masters/services/${serviceId}`)
  return data.data.service
}

/**
 * @param {number} serviceId
 * @returns {Promise<import('@/types/api').Service>}
 */
export async function duplicateService(serviceId) {
  const { data } = await api.post(`/masters/services/${serviceId}/duplicate`)
  return data.data.service
}

/**
 * @param {{ sourceBranchId: number, serviceIds?: number[] }} payload
 * @returns {Promise<{ copied: import('@/types/api').Service[], skipped: Array<{ service_id: number, name: string, reason: string }>, message: string }>}
 */
export async function copyServicesFromBranch(payload) {
  const { data } = await api.post('/masters/services/copy-from-branch', {
    source_branch_id: payload.sourceBranchId,
    service_ids: payload.serviceIds,
  })
  return {
    message: data.message,
    copied: data.data.copied,
    skipped: data.data.skipped,
  }
}

/**
 * @param {Partial<Service>} payload
 * @returns {Promise<Service>}
 */
export async function createService(payload) {
  const { data } = await api.post('/masters/services', payload)
  return data.data.service
}

/**
 * @param {number} serviceId
 * @param {Partial<Service>} payload
 * @returns {Promise<Service>}
 */
export async function updateService(serviceId, payload) {
  const { data } = await api.put(`/masters/services/${serviceId}`, payload)
  return data.data.service
}

/**
 * @param {number} serviceId
 * @returns {Promise<void>}
 */
export async function deleteService(serviceId) {
  await api.delete(`/masters/services/${serviceId}`)
}

/**
 * @param {number} serviceId
 * @param {File} file
 * @returns {Promise<Service>}
 */
export async function uploadServiceImage(serviceId, file) {
  const formData = new FormData()
  formData.append('image', file)
  const { data } = await api.post(`/masters/services/${serviceId}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data.data.service
}

/**
 * @param {number} serviceId
 * @returns {Promise<Service>}
 */
export async function deleteServiceImage(serviceId) {
  const { data } = await api.delete(`/masters/services/${serviceId}/image`)
  return data.data.service
}

/**
 * @param {string} query
 * @param {{ limit?: number, categoryId?: number }} [options]
 * @returns {Promise<Service[]>}
 */
export async function searchServices(query, options = {}) {
  const params = { q: query, ...options }
  if (options.categoryId) {
    params.category_id = options.categoryId
  }
  const { data } = await api.get('/services/search', { params })
  return data.data.services
}
